import { ThemeProvider } from '@emotion/react'
import { Grid, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material'
import { forwardRef, ForwardedRef } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { useAppSelector } from '~/app/hooks'
import { currentTheme, isDarkTheme } from '~/redux/features'
import { createCustomizedTheme, MenuProps } from '.'
import classNames from 'classnames/bind'
import styles from './EFSelect.module.scss'
import DOMPurify from 'dompurify'
import { ISelectHorizontalProps } from './EFSelect.model'

const cx = classNames.bind(styles)

export const SelectHorizontalForm = forwardRef(
  (props: ISelectHorizontalProps, _ref: ForwardedRef<unknown>) => {
    const {
      label,
      name,
      data,
      value,
      errorMessage,
      onChange,
      labelWidth = 2,
      disabled,
      ...selectProps
    } = props
    const theme = useAppSelector(currentTheme)
    const isDark = useAppSelector(isDarkTheme)
    const { control } = useFormContext()
    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Grid
            paddingBottom={2}
            container
            spacing={2}
            alignItems='center'
          >
            {label && (
              <Grid
                item
                xs={labelWidth}
                md={labelWidth}
              >
                <InputLabel
                  sx={{ whiteSpace: 'break-spaces' }}
                  htmlFor={name}
                >
                  {label}
                </InputLabel>
              </Grid>
            )}
            <Grid
              item
              xs={12}
              md={12 - labelWidth}
            >
              <ThemeProvider theme={createCustomizedTheme(isDark)}>
                <Select
                  {...field}
                  {...selectProps}
                  defaultValue={''}
                  name={field.name}
                  disabled={disabled || field.disabled}
                  labelId={field.name}
                  id={field.name}
                  value={field.value ?? ''}
                  MenuProps={MenuProps}
                  className={cx('select-field', theme)}
                  displayEmpty
                  onChange={event => {
                    const { value } = event.target
                    field.onChange(value) // Manually trigger the field's onChange event
                    if (onChange) onChange(event)
                  }}
                >
                  {props.placeholder && (
                    <MenuItem
                      value=''
                      className={cx('menu-select-item', theme)}
                    >
                      <span>{props.placeholder}</span>
                    </MenuItem>
                  )}
                  {data.map((item, index) => (
                    <MenuItem
                      className={cx('menu-select-item', theme)}
                      key={item.name + index}
                      value={item.value}
                    >
                      <span
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(item.name)
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
                {errorMessage && (
                  <FormHelperText sx={{ color: 'red !important' }}>
                    {errorMessage}
                  </FormHelperText>
                )}
              </ThemeProvider>
            </Grid>
          </Grid>
        )}
      />
    )
  }
)
